import React, { useEffect, useState, useCallback } from 'react';
import { useStore } from '../store';
import { Shell } from '../components/Shell';
import {
  AuthUser,
  UserRole,
  InvitationRow,
  listUsers,
  patchUser,
  resetUserPassword,
  deleteUser,
  createInvitation,
  listInvitations,
  revokeInvitation,
  AuthError,
} from '../lib/auth';

function initials(u: AuthUser): string {
  const base = (u.name || u.email).trim();
  const parts = base.split(/[\s.@]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || base[0]?.toUpperCase() || '?';
}

function joined(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AdminUsers() {
  const currentUser = useStore((s) => s.currentUser);
  const activeEnterpriseId = useStore((s) => s.activeEnterpriseId);

  const [users, setUsers] = useState<AuthUser[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [toReset, setToReset] = useState<AuthUser | null>(null);
  const [toDelete, setToDelete] = useState<AuthUser | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'enterprise_admin' | 'enterprise_user'>('enterprise_user');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [issuedLink, setIssuedLink] = useState<string | null>(null);

  const flash = (kind: 'ok' | 'err', text: string): void => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [u, i] = await Promise.all([listUsers(), listInvitations().catch(() => [])]);
      setUsers(u);
      setInvitations(i);
    } catch (e) {
      setError(e instanceof AuthError ? e.message : 'Failed to load users.');
    }
  }, []);

  // Re-load whenever the active workspace changes — the user list is scoped to
  // the active workspace's members, so switching must refetch.
  useEffect(() => {
    void load();
  }, [load, activeEnterpriseId]);

  const sendInvite = async (): Promise<void> => {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    try {
      const { token } = await createInvitation(inviteEmail.trim(), inviteRole);
      const link = `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`;
      setIssuedLink(link);
      setInviteEmail('');
      void load();
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Could not send invitation.');
    } finally {
      setInviteBusy(false);
    }
  };

  const revoke = async (id: string): Promise<void> => {
    try {
      await revokeInvitation(id);
      setInvitations((prev) => prev.filter((i) => i.id !== id));
      flash('ok', 'Invitation revoked.');
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Revoke failed.');
    }
  };

  const changeRole = async (u: AuthUser, role: UserRole): Promise<void> => {
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
    try {
      await patchUser(u.id, { role });
      flash('ok', `${u.name || u.email} is now ${role}.`);
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Update failed.');
      void load();
    }
  };

  const toggleActive = async (u: AuthUser): Promise<void> => {
    const next = !u.isActive;
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isActive: next } : x)));
    try {
      await patchUser(u.id, { isActive: next });
      flash('ok', next ? 'User activated.' : 'User deactivated.');
    } catch (e) {
      flash('err', e instanceof AuthError ? e.message : 'Update failed.');
      void load();
    }
  };

  return (
    <Shell active="users">
      <div className="oa-root">
      <main className="oa-main">
        <h1 className="oa-title">User Management</h1>
        <p className="oa-sub">Manage accounts, roles, and access for all internal users.</p>

        {error && <div className="oa-msg oa-msg--err" style={{ margin: '14px 0' }}>{error}</div>}

        <div className="oa-card" style={{ marginTop: 18, padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>Invite a teammate</h3>
          <p className="oa-sub" style={{ marginTop: 4 }}>
            We'll generate a one-time link. Send it to them out of band — they'll set their own password.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <input
              className="oa-input"
              type="email"
              placeholder="teammate@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              style={{ flex: '1 1 240px', minWidth: 240 }}
            />
            <select
              className="oa-select"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'enterprise_admin' | 'enterprise_user')}
            >
              <option value="enterprise_user">Member</option>
              <option value="enterprise_admin">Workspace admin</option>
            </select>
            <button className="oa-btn oa-btn--primary" disabled={inviteBusy} onClick={() => void sendInvite()}>
              {inviteBusy ? 'Issuing…' : 'Issue link'}
            </button>
          </div>
          {issuedLink && (
            <div className="oa-msg oa-msg--ok" style={{ marginTop: 12, wordBreak: 'break-all' }}>
              <div style={{ marginBottom: 6 }}>Share this one-time link (it won't be shown again):</div>
              <code style={{ fontSize: 12 }}>{issuedLink}</code>
              <div style={{ marginTop: 8 }}>
                <button
                  className="oa-btn oa-btn--sm"
                  onClick={() => { void navigator.clipboard?.writeText(issuedLink); flash('ok', 'Link copied.'); }}
                >
                  Copy
                </button>
                <button className="oa-btn oa-btn--sm" style={{ marginLeft: 8 }} onClick={() => setIssuedLink(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          {invitations.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 8 }}>Pending invitations</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {invitations.map((inv) => (
                  <li key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--oa-border, #eee)' }}>
                    <span style={{ flex: 1 }}>{inv.email}</span>
                    <span className="oa-pill">{inv.role === 'enterprise_admin' ? 'Admin' : 'Member'}</span>
                    <span className="oa-cell-muted" style={{ fontSize: 12 }}>
                      expires {new Date(inv.expiresAt).toLocaleDateString()}
                    </span>
                    <button className="oa-btn oa-btn--sm oa-btn--danger" onClick={() => void revoke(inv.id)}>
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="oa-card" style={{ marginTop: 22, overflow: 'hidden' }}>
          <div className="oa-table-head">
            {['User', 'Email', 'Joined', 'Login', 'Role', 'Active', ''].map((h, i) => (
              <span key={i} className="oa-th" style={i === 5 ? { textAlign: 'center' } : undefined}>{h}</span>
            ))}
          </div>
          {users.map((u) => {
            const isSelf = u.id === currentUser?.id;
            return (
              <div key={u.id} className="oa-row">
                <div className="oa-user-cell">
                  <span className="oa-avatar">{initials(u)}</span>
                  <span className="oa-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {u.name || u.email.split('@')[0]}
                  </span>
                </div>
                <span className="oa-cell-muted">{u.email}</span>
                <span className="oa-cell-muted">{joined(u.createdAt)}</span>
                <span><span className="oa-pill">Password</span></span>
                <span>
                  <select
                    className="oa-select"
                    value={
                      u.role === 'admin' ? 'enterprise_admin'
                      : u.role === 'internal' ? 'enterprise_user'
                      : u.role
                    }
                    disabled={isSelf}
                    onChange={(e) => void changeRole(u, e.target.value as UserRole)}
                  >
                    <option value="super_admin">Super admin</option>
                    <option value="enterprise_admin">Workspace admin</option>
                    <option value="enterprise_user">Member</option>
                  </select>
                </span>
                <span style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    className={`oa-toggle${u.isActive ? ' oa-toggle--on' : ''}`}
                    disabled={isSelf}
                    title={isSelf ? "You can't change your own status" : u.isActive ? 'Deactivate' : 'Activate'}
                    onClick={() => void toggleActive(u)}
                  >
                    <span className="oa-toggle-thumb" />
                  </button>
                </span>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="oa-btn oa-btn--sm" onClick={() => setToReset(u)}>Reset PW</button>
                  <button className="oa-btn oa-btn--sm oa-btn--danger" disabled={isSelf} onClick={() => setToDelete(u)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
          {users.length === 0 && !error && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--oa-muted)' }}>No users yet.</div>
          )}
        </div>
      </main>

      {toReset && (
        <ResetPasswordModal
          user={toReset}
          onClose={() => setToReset(null)}
          onDone={(msg) => { setToReset(null); flash('ok', msg); }}
          onError={(msg) => flash('err', msg)}
        />
      )}
      {toDelete && (
        <DeleteModal
          user={toDelete}
          onClose={() => setToDelete(null)}
          onDone={() => {
            const id = toDelete.id;
            setToDelete(null);
            setUsers((prev) => prev.filter((x) => x.id !== id));
            flash('ok', 'User deleted.');
          }}
          onError={(msg) => flash('err', msg)}
        />
      )}
      {toast && <div className={`oa-toast oa-toast--${toast.kind}`}>{toast.text}</div>}
      </div>
    </Shell>
  );
}

function ResetPasswordModal({
  user, onClose, onDone, onError,
}: { user: AuthUser; onClose: () => void; onDone: (msg: string) => void; onError: (msg: string) => void }) {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const { validatePassword } = await import('../lib/password-policy');
    const pwErr = validatePassword(pw);
    if (pwErr) { setErr(pwErr); return; }
    if (pw !== confirm) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await resetUserPassword(user.id, pw);
      onDone(`Password reset for ${user.name || user.email}.`);
    } catch (e) {
      onError(e instanceof AuthError ? e.message : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oa-backdrop oa-root" onClick={onClose}>
      <div className="oa-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Reset password</h3>
        <p className="oa-sub" style={{ marginBottom: 16 }}>Set a new password for {user.email}.</p>
        {err && <div className="oa-msg oa-msg--err" style={{ marginBottom: 12 }}>{err}</div>}
        <input className="oa-input" type="password" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus style={{ marginBottom: 10 }} />
        <input className="oa-input" type="password" placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="oa-btn" onClick={onClose}>Cancel</button>
          <button className="oa-btn oa-btn--primary" disabled={busy} onClick={() => void submit()}>{busy ? 'Saving…' : 'Set password'}</button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({
  user, onClose, onDone, onError,
}: { user: AuthUser; onClose: () => void; onDone: () => void; onError: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const remove = async (): Promise<void> => {
    setBusy(true);
    try {
      await deleteUser(user.id);
      onDone();
    } catch (e) {
      onError(e instanceof AuthError ? e.message : 'Delete failed.');
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="oa-backdrop oa-root" onClick={onClose}>
      <div className="oa-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Delete user</h3>
        <p className="oa-sub" style={{ marginBottom: 20 }}>
          Permanently delete <strong>{user.name || user.email}</strong>? This cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="oa-btn" onClick={onClose}>Cancel</button>
          <button className="oa-btn oa-btn--danger" disabled={busy} onClick={() => void remove()}>{busy ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}
